import { OrbitControls } from '@react-three/drei';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import gsap from 'gsap';
import { useFrame } from '@react-three/fiber';
import Product from './Product';

interface Props {
    modelName: string;
}

const Webgl = ({ modelName }: Props) => {
    const group = useRef<THREE.Group>(null);
    const controls = useRef<any>(null);
    const [autoRotate, setAutoRotate] = useState(false);

    const patternSvgPath = useMemo(
        () => document.querySelector<SVGEllipseElement>('ellipse.js-product-canvas-pattern-path'),
        [],
    );
    const restoreBtn = useMemo(() => document.querySelector('.js-product-canvas-pattern-btn'), []);
    const loaderEl = useMemo(() => document.querySelector<HTMLElement>('.js-product-canvas-loader'), []);

    useFrame(({ pointer }) => {
        if (group.current) {
            group.current.rotation.y = THREE.MathUtils.lerp(group.current.rotation.y, pointer.x * 0.25, 0.08);
        }
    });

    useEffect(() => {
        function restore() {
            const scope = controls.current;

            if (scope) {
                gsap.to(scope.object.position, {
                    x: scope.position0.x,
                    y: scope.position0.y,
                    z: scope.position0.z,
                    duration: 0.65,
                    ease: 'power3.out',
                    onUpdate: () => {
                        scope.object.updateProjectionMatrix();
                        scope.update();
                    },
                });
            }
        }

        restoreBtn?.addEventListener('click', restore);

        return () => {
            restoreBtn?.removeEventListener('click', restore);
        };
    }, []);

    return (
        <>
            <group ref={group}>
                <Product
                    modelName={modelName}
                    onLoad={() => {
                        if (loaderEl) {
                            loaderEl.hidden = true;
                        }
                    }}
                />
            </group>
            <OrbitControls
                ref={controls}
                autoRotate={autoRotate}
                enablePan={false}
                enableZoom={false}
                dampingFactor={0.1}
                onChange={(event) => {
                    const angle = event?.target.getAzimuthalAngle();
                    if (typeof angle === 'number') {
                        patternSvgPath?.setAttribute('stroke-dashoffset', `${-angle * 180}`);

                        if (restoreBtn) {
                            if (Math.abs(angle) > 0.1) {
                                restoreBtn.classList.add('is-visible');
                            } else {
                                restoreBtn.classList.remove('is-visible');
                            }
                        }
                    }
                }}
            />
            <ambientLight />
        </>
    );
};

export default Webgl;
